import type * as React from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { COLORS } from "components/v3/Theme/colors";
import {
  GENOMIC_LIFE_PUBLIC_PRIVACY_POLICY_URL,
  GENOMIC_LIFE_PUBLIC_TERMS_OF_USE_URL
} from "constants/externalPathConstants";
import { PATHS } from "constants/pathConstants";
import type { TypographyCustomVariantTypes } from "components/v3/Theme/TypographyVariants";

const MEDICAL_DISCLAIMER =
  "Genomic Life™ is not an insurance company and does not provide medical advice. The platform does not pay or reimburse treatment costs of any kind, nor does it provide medical treatment, issue referrals, or write prescriptions. Access to some services is dependent on a current or previous cancer diagnosis or other diagnosis or test result.";

const PRIVACY_INTRO =
  "Genomic Life takes privacy very seriously. For more information on our policies see our";

const publicPolicyLinkSx = {
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px"
} as const;

const authenticatedPolicyLinkSx = {
  color: "inherit",
  display: "inline",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": { color: COLORS.PRIMARY_DARK }
} as const;

export type GenomicLifeLegalDisclaimerProps = {
  policyLinks?: "public" | "authenticated";
  typographyVariant?: keyof TypographyCustomVariantTypes;
  textSx?: SxProps<Theme>;
  onPolicyLinkClick?: () => void;
  component?: React.ElementType;
  sx?: SxProps<Theme>;
  gap?: number;
};

const GenomicLifeLegalDisclaimer: React.FC<GenomicLifeLegalDisclaimerProps> = ({
  policyLinks = "authenticated",
  typographyVariant = "body_sm_dark_gray",
  textSx = { m: 0 },
  onPolicyLinkClick,
  component = "div",
  sx,
  gap = 2
}) => {
  const paragraphProps = {
    component: "p" as const,
    variant: typographyVariant,
    sx: textSx
  };

  return (
    <Box
      component={component}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap,
        ...sx
      }}
    >
      <Typography {...paragraphProps}>{MEDICAL_DISCLAIMER}</Typography>
      <Typography {...paragraphProps}>
        {PRIVACY_INTRO}{" "}
        {policyLinks === "public" ? (
          <>
            <Box
              component="a"
              href={GENOMIC_LIFE_PUBLIC_TERMS_OF_USE_URL}
              rel="noopener noreferrer"
              sx={publicPolicyLinkSx}
              target="_blank"
            >
              Terms of Use
            </Box>{" "}
            and{" "}
            <Box
              component="a"
              href={GENOMIC_LIFE_PUBLIC_PRIVACY_POLICY_URL}
              rel="noopener noreferrer"
              sx={publicPolicyLinkSx}
              target="_blank"
            >
              Privacy Policy
            </Box>
          </>
        ) : (
          <>
            <Box
              component={RouterLink}
              onClick={onPolicyLinkClick}
              rel="noopener noreferrer"
              sx={authenticatedPolicyLinkSx}
              target="_blank"
              to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
            >
              Terms of Use
            </Box>{" "}
            and{" "}
            <Box
              component={RouterLink}
              onClick={onPolicyLinkClick}
              rel="noopener noreferrer"
              sx={authenticatedPolicyLinkSx}
              target="_blank"
              to={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_POLICY}
            >
              Privacy Policy
            </Box>
          </>
        )}
        .
      </Typography>
    </Box>
  );
};

export default GenomicLifeLegalDisclaimer;
